import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Calendar, Badge, Select, Spin } from 'antd';
import { UndoOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  COURSE_CATEGORY_SLUGS,
  getCalendarFetchRange,
  mapEventsToCalendarSlots,
  parseTecEventDate,
} from '../utils/tecCalendarConfig';
import { fetchProductsAndAttach } from '../utils/productMatch';

const { Option } = Select;

const TEC_EVENTS_URL = 'https://pacificrimsafety.ca/wp-json/tribe/events/v1/events';
const TEC_EMBED_URL = 'https://pacificrimsafety.ca/calendar-embed/S89KcGYVJ4H/embed/';

const toCalendarDay = (value) => {
  if (!value) return dayjs(null);
  if (dayjs.isDayjs(value)) return value;
  if (typeof value.format === 'function') return dayjs(value.format('YYYY-MM-DD'));
  if (typeof value.toDate === 'function') return dayjs(value.toDate());
  return dayjs(value);
};

const fetchTecEventsFromWp = async (maxDate) => {
  const { startDate, endDate } = getCalendarFetchRange(maxDate);
  const records = [];

  const fetchPages = async (extraParams = {}) => {
    let page = 1;
    let totalPages = 1;

    while (page <= totalPages) {
      const { data } = await axios.get(TEC_EVENTS_URL, {
        params: {
          start_date: startDate,
          end_date: endDate,
          per_page: 50,
          page,
          status: 'publish',
          ...extraParams,
        },
        timeout: 60000,
      });

      records.push(...mapEventsToCalendarSlots(data?.events));

      totalPages = Number(data?.total_pages) || 1;
      page++;
    }
  };

  try {
    await fetchPages({ categories: COURSE_CATEGORY_SLUGS.join(',') });
  } catch (err) {
    console.warn('TEC categories filter failed, loading all events:', err.message);
    await fetchPages({});
  }

  return records;
};

const loadCalendarEvents = (maxDate) => fetchTecEventsFromWp(maxDate);

export default function EmbedCalender() {
  const ONE_YEAR = dayjs().add(365, 'day').format('YYYY-MM-DD');
  const [slots, setSlots] = useState([]);
  const [filteredSlots, setFilteredSlots] = useState([]);
  const [locations, setLocations] = useState([]);
  const [courseNames, setCourseNames] = useState([]);
  const [locationFilter, setLocationFilter] = useState('');
  const [courseFilter, setCourseFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [useIframe] = useState(
    () => new URLSearchParams(window.location.search).get('embed') === '1'
  );

  useEffect(() => {
    if (useIframe) return;

    setLoading(true);
    loadCalendarEvents(ONE_YEAR)
      .then(async (events) => {
        const withProducts = await fetchProductsAndAttach(events);
        setSlots(withProducts);
        setFilteredSlots(withProducts);
        setUniqueLocations(withProducts);
        setUniqueCourseNames(withProducts);
      })
      .catch((err) => console.error('Failed to fetch calendar events:', err))
      .finally(() => setLoading(false));
  }, [ONE_YEAR, useIframe]);

  useEffect(() => {
    if (!slots.length) return;

    if (!locationFilter && !courseFilter) {
      setFilteredSlots(slots);
      return;
    }

    let result = slots;
    if (locationFilter) result = handleLocationFilter(locationFilter, result);
    if (courseFilter) result = handleCourseFilter(courseFilter, result);
    setFilteredSlots(result);
  }, [locationFilter, courseFilter, slots]);

  const dateCellRender = (value) => {
    const cellDay = toCalendarDay(value);
    const cellKey = cellDay.format('YYYY-MM-DD');
    const daySlots = filteredSlots.filter(
      (slot) => slot.calendarDay === cellKey || parseTecEventDate(slot.date).format('YYYY-MM-DD') === cellKey
    );

    if (!daySlots.length) return null;

    return (
      <ul className="events" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {daySlots.map((slot, index) => {
          const dateStr = slot.calendarDay || parseTecEventDate(slot.date).format('YYYY-MM-DD');
          const name = slot.product_name || '';
          const href = slot.booking_url || slot.url;
          if (!href) return null;

          return (
            <li key={`${slot.product_id}-${dateStr}-${index}`}>
              <a
                className="calendar-event-link"
                href={href}
                target="_blank"
                rel="noreferrer"
                style={{ display: 'block' }}
              >
                {slot.location === 'Kelowna' ? (
                  <Badge color="#103B77" text={name} />
                ) : slot.location === 'Surrey' ? (
                  <Badge color="#95DE64" text={name} />
                ) : (
                  <Badge color="#2db7f5" text={name} />
                )}
              </a>
            </li>
          );
        })}
      </ul>
    );
  };

  const setUniqueLocations = (records) => {
    const locs = [...new Set(records.map((r) => r.location).filter(Boolean))].sort();
    setLocations(locs);
  };

  const setUniqueCourseNames = (records) => {
    const names = [...new Set(records.map((r) => r.product_name).filter(Boolean))].sort();
    setCourseNames(names);
  };

  const handleLocationFilter = (filter, slotsToFilter) =>
    slotsToFilter.filter((slot) => slot.location === filter);

  const handleCourseFilter = (filter, slotsToFilter) =>
    slotsToFilter.filter((slot) => slot.product_name === filter);

  const handleReset = () => {
    setLocationFilter('');
    setCourseFilter('');
  };

  if (useIframe) {
    return (
      <iframe
        data-tec-events-ece-iframe="true"
        title="Course calendar"
        src={TEC_EMBED_URL}
        frameBorder="0"
        style={{ width: '100%', maxWidth: '100%', height: 1065, border: 0 }}
      />
    );
  }

  return (
    <div>
      <Select
        placeholder="Filter by location..."
        style={{ width: 240 }}
        onChange={(selected) => setLocationFilter(selected)}
        value={locationFilter || null}
      >
        {locations.map((location) => (
          <Option key={location} value={location}>
            {location}
          </Option>
        ))}
      </Select>

      <Select
        placeholder="Filter by course..."
        style={{ width: 240 }}
        onChange={(selected) => setCourseFilter(selected)}
        value={courseFilter || null}
      >
        {courseNames.map((name) => (
          <Option key={name} value={name}>
            {name}
          </Option>
        ))}
      </Select>

      {(locationFilter || courseFilter) && (
        <UndoOutlined style={{ paddingLeft: '10px' }} onClick={handleReset} />
      )}

      <Spin spinning={loading}>
        <Calendar dateCellRender={dateCellRender} />
      </Spin>
    </div>
  );
}
